import { MathUtils, Vector3 } from 'three';
import type { PerspectiveCamera } from 'three';
import type { CollisionWorld } from '@thrasher/shared';

export interface CameraSubject {
  pos: Vector3;
  vel: Vector3;
  fwd: Vector3;
  up: Vector3;
  mode: string;
  /** Wall normal (into the bowl) while riding/airing vert. */
  wall: Vector3 | null;
}

const WORLD_UP = new Vector3(0, 1, 0);

/**
 * Chase camera: trails the rider's travel direction, swings to look out from a wall while
 * riding vert, pulls in when geometry blocks the view, and widens FOV with speed.
 */
export class CameraRig {
  private readonly position = new Vector3();
  private readonly look = new Vector3();
  private readonly back = new Vector3(0, 0, -1);
  private shake = 0;
  private initialized = false;

  public constructor(
    private readonly camera: PerspectiveCamera,
    private readonly world: CollisionWorld,
  ) {}

  public kick(amount: number): void {
    this.shake = Math.min(1.2, this.shake + amount);
  }

  public snap(): void {
    this.initialized = false;
  }

  public update(dt: number, subject: CameraSubject): void {
    const speed = subject.vel.length();
    const desiredBack = new Vector3();
    if (subject.wall) {
      // Vert: look at the wall from inside the bowl.
      desiredBack.copy(subject.wall);
    } else {
      const heading = new Vector3().copy(subject.vel);
      heading.y = 0;
      if (heading.lengthSq() < 4 || subject.mode === 'bail') {
        heading.copy(subject.fwd);
        heading.y = 0;
      }
      if (heading.lengthSq() < 1e-4) heading.copy(this.back).negate();
      desiredBack.copy(heading.normalize()).negate();
    }
    const rate = subject.wall ? 3 : subject.mode === 'air' ? 1.6 : 4;
    this.back.lerp(desiredBack, 1 - Math.exp(-rate * dt)).normalize();
    if (!this.initialized) this.back.copy(desiredBack);

    const distance = 4.3 + Math.min(2, speed * 0.07);
    const height = 1.65 + Math.min(0.7, speed * 0.025);
    const target = new Vector3().copy(subject.pos).addScaledVector(WORLD_UP, 1.15);
    const desired = new Vector3()
      .copy(target)
      .addScaledVector(this.back, distance)
      .addScaledVector(WORLD_UP, height);

    // Pull in if the park blocks the line of sight.
    const toCamera = new Vector3().subVectors(desired, target);
    const length = toCamera.length();
    toCamera.divideScalar(length);
    const hit = this.world.raycast(
      [target.x, target.y, target.z],
      [toCamera.x, toCamera.y, toCamera.z],
      length,
      true,
    );
    if (hit) desired.copy(target).addScaledVector(toCamera, Math.max(1.2, hit.t - 0.35));

    if (!this.initialized) {
      this.position.copy(desired);
      this.look.copy(target);
      this.initialized = true;
    }
    const follow = 1 - Math.exp(-(subject.mode === 'air' ? 5 : 8) * dt);
    this.position.lerp(desired, follow);
    this.look.lerp(target, 1 - Math.exp(-14 * dt));
    this.camera.position.copy(this.position);
    if (this.shake > 0) {
      this.camera.position.x += (Math.random() - 0.5) * this.shake * 0.25;
      this.camera.position.y += (Math.random() - 0.5) * this.shake * 0.25;
      this.shake = Math.max(0, this.shake - dt * 3);
    }
    this.camera.lookAt(this.look);
    const fov = 68 + MathUtils.clamp((speed - 8) * 0.6, 0, 12);
    this.camera.fov += (fov - this.camera.fov) * Math.min(1, dt * 3);
    this.camera.updateProjectionMatrix();
  }

  /** A slow orbit used behind the entry screen. */
  public orbit(time: number, center: Vector3, radius: number, height: number): void {
    const angle = time * 0.05;
    this.camera.position.set(
      center.x + Math.cos(angle) * radius,
      height,
      center.z + Math.sin(angle) * radius,
    );
    this.camera.lookAt(center);
    this.initialized = false;
  }
}
